'use client';

import { useEffect } from 'react';
import { useLocale } from 'next-intl';
import { useRouter, usePathname } from '@boty/i18n/navigation';
import { isValidLocale, type Locale } from '@boty/i18n';

const LOCALE_PREFERENCE_KEY = 'boty_locale_preference';
const SESSION_PROCESSED_KEY = 'boty_locale_processed';

/**
 * Detects and applies user's language preference on first visit.
 *
 * Priority order:
 * 1. localStorage saved preference (user explicitly chose a language)
 * 2. Browser language detection (navigator.languages)
 * 3. Keep current locale (no redirect)
 *
 * Uses sessionStorage flag to prevent redirect loops and only process once per session.
 */
export function useLanguagePreference(): void {
  const currentLocale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    // Only run on client
    if (typeof window === 'undefined') return;

    // Check if we've already processed this session
    const hasProcessed = sessionStorage.getItem(SESSION_PROCESSED_KEY);
    if (hasProcessed) return;

    // Mark as processed for this session
    sessionStorage.setItem(SESSION_PROCESSED_KEY, 'true');

    // Check localStorage for saved preference
    const savedPreference = localStorage.getItem(LOCALE_PREFERENCE_KEY);
    if (savedPreference && isValidLocale(savedPreference)) {
      if (savedPreference !== currentLocale) {
        // next-intl's router.replace accepts path + options with locale
        router.replace(pathname, { locale: savedPreference as Locale });
      }
      return;
    }

    // Detect from browser language
    const browserLocale = detectBrowserLocale();
    if (browserLocale && browserLocale !== currentLocale) {
      localStorage.setItem(LOCALE_PREFERENCE_KEY, browserLocale);
      // next-intl's router.replace accepts path + options with locale
      router.replace(pathname, { locale: browserLocale });
    }
  }, [currentLocale, pathname, router]);
}

/**
 * Detects the user's preferred locale from browser settings.
 *
 * @returns The detected locale ('en' or 'he') or null if no supported language found
 */
function detectBrowserLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;

  const languages = navigator.languages || [navigator.language];

  for (const lang of languages) {
    const langCode = lang.toLowerCase();

    // Hebrew variations (he, he-IL, iw - legacy code for Hebrew)
    if (langCode.startsWith('he') || langCode === 'iw') {
      return 'he';
    }

    // English variations
    if (langCode.startsWith('en')) {
      return 'en';
    }
  }

  // No supported language found - don't redirect
  return null;
}

export default useLanguagePreference;
